import { bindAll } from 'bind-event-listener';
import type { IntlShape } from 'react-intl';

import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { VanillaTooltip } from '@atlaskit/editor-common/vanilla-tooltip';
import { token } from '@atlaskit/tokens';

import { getAccentTokens } from '../../pm-plugins/decorations/colorSchemes/factory';
import { colorSchemeRegistry } from '../../pm-plugins/decorations/colorSchemes/schemes';
import type { ColorScheme } from '../../pm-plugins/decorations/colorSchemes/types';
import type { ContributorTagModel, ShowDiffPlugin, TagContributor } from '../../showDiffPluginType';

import {
	buildContributorTagDom,
	MIN_TAG_WIDTH,
	type ContributorTagDom,
} from './buildContributorTagDom';
import { contributorAvatarRenderer, type ContributorAvatar } from './contributorAvatarRenderer';
import { contributorTagMessages } from './messages';

export type ContributorTagControllerOptions = {
	api?: ExtractInjectionAPI<ShowDiffPlugin>;
	diffId: string;
	/**
	 * This editor's own content root (`editorView.dom`), read through the plugin's captured view.
	 *
	 * Every DOM lookup the tag makes is scoped to it, rather than resolved by class name from a
	 * highlight element: `.ProseMirror` is not this package's to rename, and with nested editors the
	 * nearest one is not necessarily the editor whose plugin rendered this tag.
	 */
	getEditorRoot: () => HTMLElement | null;
	/**
	 * Read when a model is applied rather than subscribed to, so a locale swap mid-diff would not
	 * re-label a tag already on screen. Locales do not hot-swap while a diff is open.
	 */
	getIntl: () => IntlShape;
};

const getContributorName = (
	contributor: TagContributor,
	formatMessage: IntlShape['formatMessage'],
): string => {
	const name = contributor.name?.trim();
	if (name) {
		return name;
	}

	// `agentKind` defaults to `'external'` per the plugin type contract; an unidentified external
	// agent falls back to a generic label rather than rendering a nameless tag.
	if (contributor.kind === 'agent' && (contributor.agentKind ?? 'external') === 'external') {
		return formatMessage(contributorTagMessages.externalAgentName);
	}

	return '';
};

/**
 * Resolves the tag's accent from the same scheme value and token maps the highlight used, so the
 * two can never drift.
 */
const getTagAccent = ({
	colorScheme,
	isActive,
	isInserted,
}: {
	colorScheme: ColorScheme | undefined;
	isActive: boolean;
	isInserted: boolean;
}): { background: string; border: string; text: string } => {
	const colors = colorSchemeRegistry[colorScheme ?? 'standard'];
	const accent = isInserted
		? isActive
			? colors.insertActiveColor
			: colors.insertColor
		: isActive
			? colors.deleteActiveColor
			: colors.deleteColor;

	return getAccentTokens(accent);
};

/** The line the change starts on: topmost of its fragments, leftmost among those. */
const findChangeStartLine = (highlights: Element[]): DOMRect | undefined => {
	let start: DOMRect | undefined;

	for (const highlight of highlights) {
		for (const rect of highlight.getClientRects()) {
			if (
				!start ||
				rect.top < start.top - 1 ||
				(Math.abs(rect.top - start.top) <= 1 && rect.left < start.left)
			) {
				start = rect;
			}
		}
	}

	return start;
};

/** The contributors a tag draws, keyed so a republished model can be recognised as the same pair. */
const identityOf = (model: ContributorTagModel | undefined): string =>
	[model?.contributor, model?.connectedContributor]
		.map((contributor) =>
			contributor
				? `${contributor.kind}:${contributor.agentKind ?? ''}:${contributor.name}:${contributor.avatarUrl ?? ''}`
				: '',
		)
		.join('|');

/**
 * A small tag naming who made one diff, drawn into the host element its decoration places on the
 * change's first character and positioned above it.
 *
 * The model is resolved from plugin state here rather than passed in, because ProseMirror skips
 * `toDOM` for a widget it reuses: stepping to the next change republishes the tags without redrawing
 * their hosts, so anything the tag reacts to — `isActive` above all — has to arrive through a
 * subscription of its own (EDITOR-8702). Draws nothing when this diff has no tag, which is the case
 * for the folded half of a replacement: its two decorations share the one tag.
 */
export class ContributorTagController {
	private avatars: ContributorAvatar[] = [];
	/** Guards a deferred bind that a later model change has already superseded. */
	private bindToken = 0;
	private boundSelector: string | undefined;
	private dom: ContributorTagDom | undefined;
	private fullLabel = '';
	private host: HTMLElement | undefined;
	private isDestroyed = false;
	private isHighlightHovered = false;
	private isHoveredOrFocused = false;
	private maxWidthPx: number | undefined;
	private model: ContributorTagModel | undefined;
	private resizeObserver: ResizeObserver | undefined;
	private tooltip: VanillaTooltip | undefined;
	private tooltipContent: string | undefined;
	private unbindHighlights: Array<() => void> = [];
	private unbindTag: (() => void) | undefined;
	private unsubscribe: (() => void) | undefined;

	constructor(private options: ContributorTagControllerOptions) {}

	mount(host: HTMLElement): void {
		this.host = host;
		const { sharedState } = this.options.api?.showDiff ?? {};

		this.unsubscribe = sharedState?.onChange(({ nextSharedState }) => {
			this.applyModel(this.findModel(nextSharedState?.contributorTags));
		});
		this.applyModel(this.findModel(sharedState?.currentState()?.contributorTags));
	}

	destroy(): void {
		this.isDestroyed = true;
		this.unsubscribe?.();
		this.unsubscribe = undefined;
		this.teardownTag();
		this.host = undefined;
	}

	private findModel(tags: ContributorTagModel[] | undefined): ContributorTagModel | undefined {
		return tags?.find((tag) => tag.diffId === this.options.diffId);
	}

	private applyModel(next: ContributorTagModel | undefined): void {
		if (this.isDestroyed || next === this.model) {
			return;
		}

		const previous = this.model;
		this.model = next;

		const { host } = this;
		if (!next || !host) {
			this.teardownTag();
			return;
		}

		if (!this.dom) {
			this.dom = buildContributorTagDom(host.ownerDocument);
			this.bindTag(this.dom);
			host.appendChild(this.dom.root);
		}

		if (!previous || identityOf(previous) !== identityOf(next)) {
			this.renderAvatars(next);
		}
		this.renderLabels(next);
		this.applyAccent(next);
		this.syncHighlightBindings(next);
		this.applyStyles();
		this.syncTooltip();
	}

	/** Takes the tag's DOM down, keeping the subscription so a republished model redraws it. */
	private teardownTag(): void {
		this.unbindHighlightState();
		this.boundSelector = undefined;
		this.unbindTag?.();
		this.unbindTag = undefined;
		this.tooltip?.destroy();
		this.tooltip = undefined;
		this.tooltipContent = undefined;
		this.avatars.forEach((avatar) => avatar.destroy());
		this.avatars = [];
		this.dom?.root.remove();
		this.dom = undefined;
		this.maxWidthPx = undefined;
		this.isHoveredOrFocused = false;
		// Both hover flags belong to the listeners just unbound: a republished model rebinds them, and
		// stale `true` would draw the tag as visible with the pointer nowhere near it.
		this.isHighlightHovered = false;
	}

	private bindTag({ tag }: ContributorTagDom): void {
		const setHoveredOrFocused = (isHoveredOrFocused: boolean) => () => {
			this.isHoveredOrFocused = isHoveredOrFocused;
			this.applyStyles();
		};

		this.unbindTag = bindAll(tag, [
			{ type: 'mouseover', listener: setHoveredOrFocused(true) },
			{ type: 'mouseout', listener: setHoveredOrFocused(false) },
			{ type: 'focus', listener: setHoveredOrFocused(true) },
			{ type: 'blur', listener: setHoveredOrFocused(false) },
		]);
	}

	private renderAvatars(model: ContributorTagModel): void {
		const { avatars: container } = this.dom ?? {};
		if (!container) {
			return;
		}

		this.avatars.forEach((avatar) => avatar.destroy());
		this.avatars = [];

		const doc = container.ownerDocument;
		const { background } = this.accentOf(model);
		const isPrimaryAgent = model.contributor.kind === 'agent';
		// Resolved by `kind` so a reverse `connectedTo` (an attributed user pointing at its agent)
		// renders identically.
		const user = isPrimaryAgent ? model.connectedContributor : model.contributor;
		const agent = isPrimaryAgent ? model.contributor : model.connectedContributor;

		// Hand-rolled avatar stack: `@atlaskit/avatar-group` cannot render below 24px and the tag uses
		// 16px avatars.
		const stack: Array<{ contributor: TagContributor; stackIndex?: number }> =
			model.connectedContributor
				? [
						...(user ? [{ contributor: user, stackIndex: 1 }] : []),
						...(agent ? [{ contributor: agent, stackIndex: 0 }] : []),
					]
				: [{ contributor: model.contributor }];

		stack.forEach(({ contributor, stackIndex }, index) => {
			const avatar = contributorAvatarRenderer({
				contributor,
				doc,
				ringColor: background,
				stackIndex,
			});
			if (index < stack.length - 1) {
				// Overlaps the leading avatar of a connected pair onto the one it sits in front of.
				avatar.element.style.setProperty('margin-inline-end', token('space.negative.050'));
			}
			container.appendChild(avatar.element);
			this.avatars.push(avatar);
		});
	}

	private renderLabels(model: ContributorTagModel): void {
		if (!this.dom) {
			return;
		}

		const { formatMessage } = this.options.getIntl();
		const isPrimaryAgent = model.contributor.kind === 'agent';
		const primaryName = getContributorName(model.contributor, formatMessage);
		const secondaryName = model.connectedContributor
			? getContributorName(model.connectedContributor, formatMessage)
			: '';
		const agentName = isPrimaryAgent ? primaryName : secondaryName;
		const userName = isPrimaryAgent ? secondaryName : primaryName;

		// A connected pair only ever shows the agent; the pair is spelled out in the label, because
		// the tag is never wider than the highlight.
		this.dom.name.textContent = model.connectedContributor ? agentName || primaryName : primaryName;
		this.fullLabel = model.connectedContributor
			? formatMessage(contributorTagMessages.changedByConnected, { agentName, userName })
			: formatMessage(contributorTagMessages.changedBy, { name: primaryName });
		this.dom.srLabel.textContent = this.fullLabel;
	}

	private accentOf(model: ContributorTagModel): {
		background: string;
		border: string;
		text: string;
	} {
		return getTagAccent({
			colorScheme: model.colorScheme,
			isActive: Boolean(model.isActive),
			isInserted: model.isInserted ?? true,
		});
	}

	private applyAccent(model: ContributorTagModel): void {
		if (!this.dom) {
			return;
		}

		const accent = this.accentOf(model);
		this.dom.tag.style.setProperty('background-color', accent.background);
		this.dom.tag.style.setProperty('border-bottom-color', accent.border);
		this.dom.name.style.setProperty('color', accent.text);
		this.avatars.forEach((avatar) => avatar.setRingColor(accent.background));
	}

	/** Width clamp and visibility — the two things that are not carried by the model alone. */
	private applyStyles(): void {
		if (!this.dom) {
			return;
		}

		this.dom.root.style.setProperty(
			'max-width',
			// Clamped to the highlight's own width, floored at `MIN_TAG_WIDTH` so a one-character
			// change still shows an avatar.
			this.maxWidthPx === undefined
				? ''
				: `max(${Math.round(this.maxWidthPx)}px, ${MIN_TAG_WIDTH})`,
		);

		const isVisible =
			Boolean(this.model?.isActive) || this.isHighlightHovered || this.isHoveredOrFocused;
		this.dom.tag.style.setProperty('opacity', isVisible ? '1' : '0');
		this.dom.tag.style.setProperty('pointer-events', isVisible ? 'auto' : 'none');
	}

	/**
	 * The full label is always the tooltip, whether or not the name it draws is clipped: the tag is
	 * never wider than the highlight, so a name that fits at one width is ellipsised at the next, and
	 * a connected pair spells out a contributor the tag does not show at any width.
	 *
	 * `VanillaTooltip` has no "set new content" call, so it is rebuilt when the label changes — the
	 * shape of `syncVanillaDisabledTooltip` in `mentionNodeView`.
	 */
	private syncTooltip(): void {
		const content = this.dom ? this.fullLabel : undefined;

		if (content === this.tooltipContent) {
			return;
		}

		this.tooltip?.destroy();
		this.tooltip = undefined;
		this.tooltipContent = content;

		if (!content || !this.dom) {
			return;
		}

		this.tooltip = new VanillaTooltip(
			this.dom.tag,
			content,
			undefined,
			// `ak-editor-vanilla-tooltip-default` opts into the shared `VanillaTooltip` look, defined as
			// `vanillaTooltipDefaultStyles` in both EditorContentContainer stylesheets — keep in sync when
			// renaming. Those styles are scoped under `.ProseMirror`, and the tag renders inside it.
			'ak-editor-vanilla-tooltip-default',
		);
		// `VanillaTooltip` sets `aria-describedby` on its trigger, which would announce the label a
		// second time — the visually hidden child already announces it, after the change as design
		// requires.
		this.dom.tag.removeAttribute('aria-describedby');
	}

	/**
	 * Observes the highlight this tag describes, for what CSS here cannot see: its hover state (the
	 * highlight is a ProseMirror decoration in a different DOM subtree, so listeners are bound
	 * imperatively via `data-diff-id`) and the width the tag is clamped to.
	 *
	 * Deliberately not routed through plugin state: a transaction per pointer move would re-run the
	 * memoised decoration calculation.
	 */
	private syncHighlightBindings(model: ContributorTagModel): void {
		const { diffId } = this.options;
		const selector = [diffId, ...(model.linkedDiffIds ?? [])]
			.map((id) => `[data-diff-id="${id}"]`)
			.join(', ');

		if (selector === this.boundSelector) {
			return;
		}

		this.boundSelector = selector;
		this.unbindHighlightState();

		// ProseMirror draws the tag's host in the same pass as the highlights the tag measures, so
		// those may not be in the document yet. A microtask lands once that pass has finished.
		const pendingBind = ++this.bindToken;
		queueMicrotask(() => {
			if (!this.isDestroyed && pendingBind === this.bindToken) {
				this.bindHighlightState(selector);
			}
		});
	}

	private bindHighlightState(selector: string): void {
		// Both queries are scoped to this editor's content root, so a tag can only ever bind to the
		// decorations its own plugin instance rendered — another editor on the page, or one nested
		// inside this one, cannot supply the geometry.
		const editorRoot = this.options.getEditorRoot();
		const highlights = editorRoot?.querySelectorAll(selector);
		if (!editorRoot || !highlights?.length) {
			return;
		}

		// The width comes from this tag's OWN decoration; sibling halves of the change are here for
		// hover only.
		const ownElements = Array.from(
			editorRoot.querySelectorAll(`[data-diff-id="${this.options.diffId}"]`),
		);

		const measure = () => {
			// Counted by distinct line box, not by client rect: an inline node inside the change (a
			// mention, emoji, date) splits the highlight into several rects on the same line, and
			// treating that as a wrap would drop the width clamp on changes that never wrapped.
			const lineTops = new Set<number>();
			for (const highlight of ownElements) {
				for (const rect of highlight.getClientRects()) {
					lineTops.add(Math.round(rect.top));
				}
			}

			// A wrapped change's first line says nothing useful about how wide the tag may be.
			this.maxWidthPx = lineTops.size > 1 ? undefined : findChangeStartLine(ownElements)?.width;
			this.applyStyles();
		};
		measure();

		// An inline element cannot be observed itself (ResizeObserver skips boxes with no principal
		// box), so the block it sits in is observed instead.
		this.resizeObserver = new ResizeObserver(measure);
		for (const parent of new Set(ownElements.map(({ parentElement }) => parentElement))) {
			if (parent) {
				this.resizeObserver.observe(parent);
			}
		}

		// `bindAll` takes a single target, and a change can render more than one highlight.
		this.unbindHighlights = Array.from(highlights).map((highlight) =>
			bindAll(highlight, [
				{
					type: 'mouseenter',
					listener: () => {
						this.isHighlightHovered = true;
						this.applyStyles();
					},
				},
				{
					type: 'mouseleave',
					listener: () => {
						this.isHighlightHovered = false;
						this.applyStyles();
					},
				},
			]),
		);
	}

	private unbindHighlightState(): void {
		this.resizeObserver?.disconnect();
		this.resizeObserver = undefined;
		this.unbindHighlights.forEach((unbind) => unbind());
		this.unbindHighlights = [];
	}
}
